import { useState, useEffect, useContext, useRef } from "react";
import { useSearchParams, Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import UserContext from "../../../context/UserContext";
import PageNavigation from "../../pageNavigation/PageNavigation";
import { getPostBaseUrl, getPostCategoryColor, getPostCategoryName, isOnlyAdmin } from "../../../constant/PostClass";
import PostApi from "../../../api/PostApi";
import { loginAlert } from "../../common/CustomAlert";
import { getRemovedTagContent } from "../../../util/StringUtil";
import { TodayTimestampDisplay } from "../../common/TimestampDisplay";
import { shouldShowWriterMedal } from "../../../util/MembershipUtil";
import { resolvePostThumbnailUrl } from "../../../util/ImageService";

const PAGE_SIZE = 20;

const getParams = (search, key) => {
    const params = new URLSearchParams(search);
    return params.get(key);
}

const getBaseUrl = type => {
    const postType = type.length === 5 ? type : type + '0';
    return getPostBaseUrl(postType);
}

const findCategory = (categories, postType) => {
    const category = categories.find((category) => category.postType === postType);
    return category ? category : categories[0];
}

const BoardContainer = props => {
    const { postType = '', postLabel = '', usePinPost = false, postSectionTitle, children, categories } = props;
    const [ searchParams, setSearchParams ] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(UserContext);
    const type = searchParams.get('postType');
    const label = searchParams.get('postLabel');
    const [ category, setCategory ] = useState(findCategory(categories, type));
    const [ isSearch, setIsSearch ] = useState(false);
    const [ posts, setPosts ] = useState([]);
    const [ page, setPage ] = useState(1);
    const [ searchInput, setSearchInput ] = useState('');
    const [ totalCnt, setTotalCnt ] = useState(0);
    const [ annoucePosts, setAnnouncePosts ] = useState([]);
    const sectionAreaRef = useRef(null);

    const handleSearchInput = e => setSearchInput(e.target.value);
    
    const getPosts = async (props = {}) => {
        const { 
            newPage = page,
            newSize = PAGE_SIZE,
            newSearchKeyword = searchInput
        } = props;
        const rslt = await PostApi.getPosts(postType, newPage, newSize, false, null, newSearchKeyword, postLabel);
        setPosts(rslt.posts);
        setIsSearch((newSearchKeyword || '').trim().length > 0);
        getTotalCount(newSearchKeyword);
    }
    
    const handleSearchInputKeyup = e => {
        if (e.which !== 13) return;
        handleSearchBtn();
    }

    const handleSearchBtn = (e, keywordFromTotalSearch) => {
        const newSearchKeyword = keywordFromTotalSearch ? keywordFromTotalSearch : searchInput.trim();
        setPage(1);
        getPosts({ newPage : 1, newSearchKeyword });
        setSearchInput(newSearchKeyword);
    }

    const handleWriteBtn = () => {
        if (!user.userId) return loginAlert('로그인이 필요합니다다.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const endfix = ['PT00', 'PT01'].includes(postType) ? '1' : '0';
        const navPostType = postType.length === 4 ? postType + endfix : postType;
        navigate(`/post/write?postType=${navPostType}`);
    }

    const handleSearchCloseBtn = () => {
        setSearchInput('');
        setPage(1);
        getPosts({ newPage: 1, newSearchKeyword: '' });
    }

    const getTotalCount = async (newSearchKeyword = searchInput) => {
        const rslt = await PostApi.getPostTotalCount(newSearchKeyword, postType, postLabel);
        setTotalCnt(rslt.totalCnt);
    }

    const handlePageChange = newPage => {
		if (!newPage) return;
		setPage(newPage);
        if (sectionAreaRef.current) {
            const headerOffset = 100;
            const elementTop = sectionAreaRef.current.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({ top: elementTop - headerOffset, behavior: 'smooth' });
        }
	}

    const getAnnoucePosts = async () => {
        if (location.pathname === '/') return;
        const rslt = await PostApi.getAnnoucePosts(postType);
        if (rslt && rslt.posts) setAnnouncePosts(rslt.posts);
    }

    const isAdmin = () => {
        if (!isOnlyAdmin(postType)) return true;
        return user && user.userId && user.userType === 'admin';
    }

    useEffect(() => {
        setSearchInput('');
        setPage(1);
        getAnnoucePosts();
        getPosts({ newPage: 1, newSearchKeyword: '' });
        const handleTotalSearchEvent = e => {
            handleSearchBtn(e, e.detail.totalSearchKeyword);
        }
        document.addEventListener("totalSearch", handleTotalSearchEvent);
        return () => {
            document.removeEventListener("totalSearch", handleTotalSearchEvent);
        }
    }, [postType, postLabel]);

    useEffect(() => {
        if (page === 1) return;
        getPosts({ newPage: page, newSearchKeyword: searchInput });
    }, [page]);

    useEffect(() => {
        if (getParams(location.search, 'reload') === 'true') {
            getPosts({ newPage: page, newSearchKeyword: searchInput });
        }
    }, [location.search]);


    return (
        <div className="vh-content">
            <Outlet />
			        <div className="section-area" ref={sectionAreaRef}>
                <div className="section-header">
                    <div className="section-header-right">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="검색어를 입력해주세요"
                                className="search-url"
                                value={searchInput}
                                onChange={handleSearchInput}
                                onKeyUp={handleSearchInputKeyup}
                            />
                            <button type="button" className="btn btn-primary" onClick={handleSearchBtn}>
                                <i className="icon icon-search-bold"></i>
                            </button>
                        </div>
                    </div>
                </div>
                {isSearch && (
                    <div className="search-result-summary">
                        <p className="end_txt">검색결과 {totalCnt}건</p>
                        <button
                            type="button"
                            className="search-result-close"
                            onClick={handleSearchCloseBtn}
                        >
                            검색닫기
                        </button>
                    </div>
                )}
                <div className="section-content">
                    <div className="category-list-area">
                        <ul>
                            {posts.map(post => {
                                const thumbnailUrl = resolvePostThumbnailUrl(post);
                                return (
                                <li key={post.post_id}>
                                    <Link to={`${getBaseUrl(post.post_type)}/post/detail/${post.post_id}`} className="category-list-link">
                                        <div className="category-list-text">
                                            <span className={`category-list-label ${getPostCategoryColor(post.post_type)}`}>
                                                {getPostCategoryName(post.post_type)}
                                            </span>
                                            <div className="category-list-tit els">{post.post_title}</div>
                                            <p className="els2">
                                                {getRemovedTagContent(post.content)}
                                            </p>

                                            <ul className="category-list-meta">
                                                <li>
                                                    <div className="category-list-meta-item">
                                                        {shouldShowWriterMedal(post) && (
                                                            <i className="icon icon-medal"></i>
                                                        )}
                                                        <span>{post.post_writer_nicknm || post.user_nicknm}</span>
                                                    </div>
                                                </li>
                                                <li>
                                                    <span><TodayTimestampDisplay timestamp={post.post_reg_date}/></span>
                                                </li>
                                                <li>
                                                    <div className="icon_btn">
                                                        <i className="icon icon-heart"></i>
                                                        <span>{post.post_like_cnt || 0}</span>
                                                    </div>
                                                    <div className="icon_btn">
                                                        <i className="icon icon-chat"></i>
                                                        <span>{post.post_comment_cnt || 0}</span>
                                                    </div>
                                                    <div className="icon_btn">
                                                        <i className="icon icon-eye"></i>
                                                        <span>{post.post_visit || 0}</span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                        {thumbnailUrl && (
                                            <div className="category-list-thumb">
                                                <img src={thumbnailUrl} alt="썸네일" />
                                            </div>
                                        )}
                                    </Link>
                                </li>
                            );})}
                        </ul>
                    </div>
                </div>
                <PageNavigation postType={type} totalCount={totalCnt} showCount={PAGE_SIZE} onChange={handlePageChange} />
            </div>
        </div>
    )
}

export default BoardContainer;